import { useId } from "react";
import type { AppearanceChoice } from "@townsy/contracts";
import { APPEARANCE_GROUPS, applyChoice } from "./avatarModel";
import { AvatarPreview } from "./AvatarPreview";

/**
 * Avatar builder (ADR-0004 #12): Body, Shirt color, Skin tone, Hair color, Hair style, Outfit.
 * Each group is a real radio group (fieldset + legend, arrow keys move within a group) styled as
 * swatches or pills, with the contract's display labels. The preview updates on every change.
 * Only approved catalogue ids can be chosen; the server validates again.
 */
export function AvatarBuilder({
  value,
  onChange,
  disabled = false,
}: {
  value: AppearanceChoice;
  onChange(next: AppearanceChoice): void;
  disabled?: boolean;
}) {
  const uid = useId();
  return (
    <div className="avatar-builder" data-testid="avatar-builder">
      <div className="avatar-builder-preview">
        <AvatarPreview choice={value} />
        <span className="hint">Preview. Your avatar in town uses the same choices.</span>
      </div>
      <div className="avatar-builder-groups">
        {APPEARANCE_GROUPS.map((group) => {
          const current = value[group.field];
          return (
            <fieldset key={group.field} className="choice-group" disabled={disabled}>
              <legend>
                {group.legend}: <span className="muted">{(group.labels as Record<string, string>)[current]}</span>
              </legend>
              <div className="choice-options">
                {group.options.map((opt) => {
                  const label = (group.labels as Record<string, string>)[opt] ?? opt;
                  const swatch = group.swatch ? (group.swatch as Record<string, string>)[opt] : null;
                  return (
                    <label key={opt} className={swatch ? "choice choice-swatch" : "choice choice-pill"} title={label}>
                      <input
                        type="radio"
                        className="choice-input"
                        name={`${uid}-${group.field}`}
                        value={opt}
                        checked={current === opt}
                        onChange={() => onChange(applyChoice(value, group.field, opt))}
                        data-testid={`avatar-${group.field}-${opt}`}
                      />
                      {swatch ? (
                        <>
                          <span className="choice-dot" style={{ background: swatch }} aria-hidden="true" />
                          <span className="visually-hidden">{label}</span>
                        </>
                      ) : (
                        <span className="choice-text">{label}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
